import { useState } from "react";
import { Link } from "react-router-dom";
import { FaChevronDown, FaArrowLeft } from "react-icons/fa";
import "./Policies.css";
import usePageMeta from "../hooks/usePageMeta";
import { pageTitle } from "../lib/seo";
import { useBrand } from "../brand/BrandContext";

/* Policy text, filled in with the site's brand name and domain. */
const buildSections = (brand) => {
  const domain = String(brand.siteUrl || "").replace(/^https?:\/\//, "").replace(/\/+$/, "");
  return [
  {
    id: "shipping",
    title: "Shipping & Delivery Policy",
    subtitle: "Digital & Travel Services",
    blocks: [
      { heading: "Overview", body: `${brand.shortName} provides travel-related services which are delivered digitally or via confirmation documents. No physical shipping of goods is involved.` },
      { heading: "Delivery Method", list: [
        "Booking confirmations are sent via email, WhatsApp, or SMS.",
        "Travel documents such as vouchers, tickets, and itineraries are delivered digitally."
      ]},
      { heading: "Delivery Timelines (TAT)", list: [
        "Flight / Hotel Bookings: Within 24–72 hours after payment confirmation",
        "Customized Tour Packages: 2–5 working days after itinerary finalization",
        "Visa Assistance Documents: As per embassy processing timelines (varies by country)"
      ]},
      { heading: "Delays", body: "Delays may occur due to:", list: [
        "Supplier confirmations (airlines / hotels)",
        "Visa processing time",
        "Public holidays or technical issues"
      ]},
    ],
  },
  {
    id: "refund",
    title: "Refund & Cancellation Policy",
    blocks: [
      { heading: "Cancellation Policy", list: [
        "Cancellation requests must be made in writing via email or official communication.",
        "Cancellation charges depend on service provider policies (airlines, hotels, visa centers, etc.)."
      ]},
      { heading: "Refund Policy", body: "Refunds (if applicable) will be processed after deduction of cancellation charges and service fees.", list: [
        "7–15 working days for domestic bookings",
        "15–30 working days for international bookings"
      ]},
      { heading: "Non-Refundable Services", list: [
        "Visa fees",
        "Certain discounted or promotional bookings",
        "Last-minute bookings as specified at time of confirmation"
      ]},
      { heading: "No Show Policy", body: "No refund will be provided for no-shows or unused services." },
      { heading: "Force Majeure", body: "No refunds will be issued for cancellations due to uncontrollable events (weather, strikes, government restrictions, etc.)." },
    ],
  },
  {
    id: "privacy",
    title: "Privacy Policy",
    blocks: [
      { heading: "Introduction", body: `${brand.shortName} respects your privacy and is committed to protecting your personal data.` },
      { heading: "Information We Collect", body: "We may collect:", list: [
        "Name, contact number, email address",
        "Passport details (for visa / travel bookings)",
        "Payment and billing details",
        "Travel preferences"
      ]},
      { heading: "How We Use Your Information", body: "We use your data to:", list: [
        "Process bookings",
        "Provide travel services",
        "Send confirmations and updates",
        "Improve customer experience",
        "Comply with legal requirements"
      ]},
      { heading: "Data Sharing", list: [
        "We may share information with airlines, hotels, visa authorities, and transport providers.",
        "We do NOT sell personal data to third parties."
      ]},
      { heading: "Data Security", body: "We use appropriate security measures to protect your data against unauthorized access or misuse." },
      { heading: "Cookies", body: "Our website may use cookies to improve browsing experience and analytics." },
      { heading: "User Rights", body: "You may request access, correction, or deletion of your data by contacting us." },
    ],
  },
  {
    id: "terms",
    title: "Terms & Conditions",
    blocks: [
      { heading: "Introduction", body: `Welcome to ${brand.shortName} (${domain}). By accessing or using our website and services, you agree to comply with and be bound by the following Terms & Conditions. If you do not agree, please do not use our services.` },
      { heading: "Services", body: "We provide travel-related services including tour packages, visa assistance, hotel bookings, flight bookings, transportation, and travel consultation." },
      { heading: "Booking Policy", list: [
        "All bookings are subject to availability.",
        "Booking confirmation will be provided only after receipt of required payment.",
        "Prices are subject to change without prior notice until booking is confirmed."
      ]},
      { heading: "Payment Terms", list: [
        "Full or partial advance payment may be required for confirmation.",
        "Payment can be made via bank transfer, UPI, credit / debit cards, or approved payment gateways.",
        "All payments must be made in INR unless otherwise specified."
      ]},
      { heading: "Cancellations", body: "Cancellations are subject to our Refund & Cancellation Policy above." },
      { heading: "User Responsibilities", list: [
        "Customers must provide accurate personal and travel information.",
        "Any incorrect information may lead to cancellation or service denial without refund."
      ]},
      { heading: "Limitation of Liability", body: "We are not responsible for:", list: [
        "Flight / train delays or cancellations",
        "Visa rejections",
        "Force majeure events (natural disasters, political unrest, etc.)"
      ]},
      { heading: "Changes to Itinerary", body: "We reserve the right to modify itineraries due to operational, weather, or safety reasons." },
      { heading: "Governing Law", body: "All disputes are subject to the jurisdiction of courts in India." },
    ],
  },
  ];
};

export default function Policies() {
  const brand = useBrand();
  const SECTIONS = buildSections(brand);
  usePageMeta({
    title: pageTitle("Terms, Privacy & Refund Policy"),
    description: `Terms and conditions, privacy policy, refund and cancellation policy for bookings made with ${brand.name}.`,
  });
  // Auto-open the section if URL has a hash, otherwise first one
  const initial = (typeof window !== "undefined" && window.location.hash.replace("#", "")) || "shipping";
  const [open, setOpen] = useState(initial);

  function toggle(id) {
    setOpen((cur) => (cur === id ? null : id));
    // Scroll the opened section into view on mobile
    setTimeout(() => {
      const el = document.getElementById(`policy-${id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }

  return (
    <div className="policies-page">
      <div className="policies-container">
        <Link to="/" className="policies-back" data-testid="policies-back">
          <FaArrowLeft /> Back to home
        </Link>

        <h1 className="policies-h1">Policies &amp; Information</h1>
        <p className="policies-intro">
          Please review the policies below before booking. By using our services you agree to these terms.
        </p>

        <div className="policies-quicklinks">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              onClick={(e) => { e.preventDefault(); setOpen(s.id); document.getElementById(`policy-${s.id}`)?.scrollIntoView({ behavior: "smooth" }); }}
              className="policies-pill"
              data-testid={`policy-link-${s.id}`}
            >
              {s.title}
            </a>
          ))}
        </div>

        {SECTIONS.map((s) => {
          const isOpen = open === s.id;
          return (
            <section key={s.id} id={`policy-${s.id}`} className={`policy-section ${isOpen ? "open" : ""}`}>
              <button
                onClick={() => toggle(s.id)}
                className="policy-toggle"
                aria-expanded={isOpen}
                data-testid={`policy-toggle-${s.id}`}
              >
                <span>
                  <span className="policy-title">{s.title}</span>
                  {s.subtitle && <span className="policy-subtitle">{s.subtitle}</span>}
                </span>
                <FaChevronDown className={`policy-chevron ${isOpen ? "rotated" : ""}`} />
              </button>

              {isOpen && (
                <div className="policy-body">
                  {s.blocks.map((b, i) => (
                    <div key={i} className="policy-block">
                      <h3>{b.heading}</h3>
                      {b.body && <p>{b.body}</p>}
                      {b.list && (
                        <ul>
                          {b.list.map((li, j) => <li key={j}>{li}</li>)}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        })}

        <div className="policies-contact-card">
          <h3>Questions about these policies?</h3>
          <p>Reach out to us at <a href={`mailto:${brand.supportEmail || brand.email}`}>{brand.supportEmail || brand.email}</a></p>
        </div>
      </div>
    </div>
  );
}
